import React, {
  forwardRef,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import axios from 'axios';
import { Link, useParams } from 'react-router-dom';
import moment from 'moment';
import MaterialTable from 'material-table';
import { createTheme, makeStyles, ThemeProvider } from '@material-ui/core/styles';

import AddBox from '@material-ui/icons/AddBox';
import ArrowUpward from '@material-ui/icons/ArrowUpward';
import Check from '@material-ui/icons/Check';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import ChevronRight from '@material-ui/icons/ChevronRight';
import Clear from '@material-ui/icons/Clear';
import DeleteOutline from '@material-ui/icons/DeleteOutline';
import Edit from '@material-ui/icons/Edit';
import FilterList from '@material-ui/icons/FilterList';
import FirstPage from '@material-ui/icons/FirstPage';
import LastPage from '@material-ui/icons/LastPage';
import Remove from '@material-ui/icons/Remove';
import SaveAlt from '@material-ui/icons/SaveAlt';
import Search from '@material-ui/icons/Search';
import ViewColumn from '@material-ui/icons/ViewColumn';

import { AuthorizeContext } from '../RootContext';
import { api_url } from '../authorize';
import logo from './../img/lento_logo_transparent.png';

const tableIcons = {
  Add: forwardRef((props, ref) => <AddBox {...props} ref={ref} />),
  Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
  Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
  DetailPanel: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
  Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
  Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
  FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
  LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
  NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  PreviousPage: forwardRef((props, ref) => <ChevronLeft {...props} ref={ref} />),
  ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
  SortArrow: forwardRef((props, ref) => <ArrowUpward {...props} ref={ref} />),
  ThirdStateCheck: forwardRef((props, ref) => <Remove {...props} ref={ref} />),
  ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />),
};

const useStyles = makeStyles((theme) => ({
  page: {
    minHeight: 'calc(100vh - 64px)',
    padding: '18px 10px 44px',
    boxSizing: 'border-box',
    background:
      'radial-gradient(circle at 8% 0%, rgba(189, 84, 84, 0.10), transparent 28%), linear-gradient(180deg, #f8fafc 0%, #eef3f6 100%)',
    color: '#173247',

    [theme.breakpoints.down('sm')]: {
      padding: '14px 8px 30px',
    },
  },

  shell: {
    width: '100%',
    maxWidth: 'none',
    margin: '0 auto',
  },

  hero: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 20,
    marginBottom: 20,
    padding: '27px 30px',
    borderRadius: 18,
    border: '1px solid rgba(255, 255, 255, 0.24)',
    background: 'linear-gradient(135deg, #173e59 0%, #6f3f4b 58%, #a64f4f 100%)',
    boxShadow: '0 16px 36px rgba(80, 45, 54, 0.18)',

    [theme.breakpoints.down('sm')]: {
      alignItems: 'flex-start',
      flexDirection: 'column',
      padding: '20px 18px',
    },
  },

  eyebrow: {
    margin: 0,
    color: 'rgba(255, 255, 255, 0.72)',
    fontSize: 12.5,
    fontWeight: 900,
    letterSpacing: '0.13em',
    textTransform: 'uppercase',
  },

  title: {
    margin: '5px 0 0',
    color: '#ffffff',
    fontSize: 36,
    lineHeight: 1.15,
    fontWeight: 850,
    letterSpacing: '-0.025em',
  },

  subtitle: {
    margin: '7px 0 0',
    color: 'rgba(255, 255, 255, 0.82)',
    fontSize: 16,
    lineHeight: 1.55,
  },

  countBadge: {
    flexShrink: 0,
    minWidth: 132,
    padding: '15px 20px',
    borderRadius: 14,
    border: '1px solid rgba(255, 224, 224, 0.32)',
    background: 'rgba(255, 229, 229, 0.13)',
    color: '#ffffff',
    textAlign: 'center',
    backdropFilter: 'blur(6px)',
  },

  countValue: {
    display: 'block',
    fontSize: 32,
    lineHeight: 1,
    fontWeight: 900,
  },

  countLabel: {
    display: 'block',
    marginTop: 5,
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },

  tableCard: {
    overflow: 'hidden',
    borderRadius: 18,
    border: '1px solid #d8e5ea',
    background: '#ffffff',
    boxShadow: '0 16px 38px rgba(23, 70, 94, 0.10)',

    '& > .MuiPaper-root': {
      borderRadius: '0 !important',
      boxShadow: 'none !important',
    },

    '& .MuiToolbar-root': {
      minHeight: '82px !important',
      padding: '13px 24px !important',
      borderBottom: '1px solid #e1eaee',
      background: '#ffffff',
    },

    '& .MuiToolbar-root h6': {
      color: '#173247',
      fontSize: '24px !important',
      fontWeight: '850 !important',
    },

    '& .MuiInputBase-root': {
      minHeight: 48,
      borderRadius: 10,
      color: '#234154',
      fontSize: 16,
    },

    '& .MuiToolbar-root .MuiInputBase-root': {
      minWidth: 280,
    },

    [theme.breakpoints.down('sm')]: {
      '& .MuiToolbar-root': {
        padding: '10px 12px !important',
      },
      '& .MuiToolbar-root .MuiInputBase-root': {
        minWidth: 180,
      },
    },

    '& .MuiInput-underline:before': {
      borderBottomColor: '#c9d9e0',
    },

    '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
      borderBottomColor: '#b06464',
    },

    '& .MuiIconButton-root': {
      padding: 12,
      color: '#496a7c',
    },

    '& .MuiSvgIcon-root': {
      fontSize: '1.55rem',
    },

    '& .MuiTableCell-root': {
      borderBottomColor: '#e3ebef',
    },

    '& .MuiTablePagination-root': {
      color: '#415f71',
      fontSize: 15,
    },

    '& .MuiTablePagination-caption, & .MuiTablePagination-selectRoot': {
      fontSize: '15px !important',
    },
  },

  caseLink: {
    color: '#087b98',
    fontSize: 16.5,
    fontWeight: 850,
    textDecoration: 'none',

    '&:hover': {
      color: '#055e75',
      textDecoration: 'underline',
    },
  },

  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 108,
    minHeight: 36,
    padding: '6px 14px',
    borderRadius: 999,
    border: '1px solid #ecc3c3',
    background: '#fff0f0',
    color: '#a33a3a',
    fontSize: 15,
    fontWeight: 850,
  },

  loadingScreen: {
    position: 'fixed',
    top: 72,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 1200,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    background: '#ffffff',
  },

  loadingLogoWrap: {
    width: 'min(680px, 62vw)',
    maxWidth: 'calc(100vw - 48px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    [theme.breakpoints.down('sm')]: {
      width: 'min(520px, 78vw)',
    },

    [theme.breakpoints.down('xs')]: {
      width: 'min(390px, 86vw)',
    },
  },

  loadingLogo: {
    display: 'block',
    width: '100%',
    height: 'auto',
    objectFit: 'contain',
    background: 'transparent',
    filter: 'none',
  },

  errorCard: {
    maxWidth: 620,
    margin: '70px auto',
    padding: '24px',
    borderRadius: 16,
    border: '1px solid #edc8c4',
    background: '#fff5f4',
    color: '#8e4039',
    boxShadow: '0 16px 38px rgba(86, 38, 34, 0.08)',
    fontSize: 15,
    fontWeight: 750,
    textAlign: 'center',
  },
}));

const theme = createTheme({
  palette: {
    primary: {
      main: '#9a4a4a',
    },
  },
  typography: {
    fontFamily: 'Arial, Helvetica, sans-serif',
    h6: {
      fontSize: '1.5rem',
      fontWeight: 800,
    },
  },
});

const formatDate = (value) => {
  if (!value) return '—';
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format('MMM DD, YYYY') : String(value);
};

const formatName = (person) => {
  if (!person || typeof person !== 'object') return '—';
  return [person.Last_Name, person.First_Name].filter(Boolean).join(', ') || '—';
};

const AbortList = () => {
  const classes = useStyles();
  const { token, role, setAuthBody, setAuthenticated } = useContext(AuthorizeContext);
  const { surgeonid } = useParams();

  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const roleName = String(role || '');
  const roleIncludes = (value) => roleName.includes(value);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        let url;

        if (roleIncludes('admin')) {
          url = `https://${api_url}/api/case_list?status=abort`;
        } else if (roleIncludes('hospital')) {
          url = `https://${api_url}/api/hospital_list?for_hospital=true&status=abort`;
        } else if (roleIncludes('distributor')) {
          url = `https://${api_url}/api/distributor_list?for_distributor=true&status=abort`;
        } else {
          url = `https://${api_url}/api/case_list?surgeon=${surgeonid || ''}&status=abort`;
        }

        const response = await axios({
          url,
          method: 'get',
          headers: {
            'x-access-token': token,
          },
        });

        if (!isMounted) return;

        if (response.data && response.data.notLoggedIn) {
          setAuthenticated(false);
          setAuthBody({ role: 'visitor' });
          alert('You have been logged out due to inactivity. Please log in again.');
          return;
        }

        if (response.data && response.data.message) {
          setLoadError(response.data.message);
          return;
        }

        setTableData(Array.isArray(response.data) ? response.data : []);
      } catch (requestError) {
        if (!isMounted) return;

        setLoadError(
          (requestError.response
            && requestError.response.data
            && requestError.response.data.message)
            || requestError.message
            || 'Unable to load aborted cases.'
        );
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [role, setAuthBody, setAuthenticated, surgeonid, token]);

  const columns = useMemo(() => [
    {
      title: 'Case #',
      field: 'case_id',
      editable: 'never',
      render: (rowData) => (
        <Link className={classes.caseLink} to={`/case/${rowData.case_id}`}>
          {rowData.case_id || '—'}
        </Link>
      ),
      cellStyle: {
        width: 130,
      },
    },
    {
      title: 'Surgeon Name',
      field: 'Surgeon.Last_Name',
      editable: 'never',
      render: (rowData) => formatName(rowData.Surgeon),
    },
    {
      title: 'Patient Name',
      field: 'Patient.Last_Name',
      editable: 'never',
      render: (rowData) => formatName(rowData.Patient),
    },
    {
      title: 'DOPO',
      field: 'dateCreated',
      editable: 'never',
      type: 'date',
      render: (rowData) => formatDate(rowData.dateCreated),
      customSort: (a, b) => moment(a.dateCreated).valueOf() - moment(b.dateCreated).valueOf(),
      cellStyle: {
        whiteSpace: 'nowrap',
      },
    },
    {
      title: 'DOS',
      field: 'Patient.Date_of_Surgery',
      editable: 'never',
      type: 'date',
      defaultSort: roleIncludes('surgeon') ? 'asc' : undefined,
      render: (rowData) => formatDate(rowData.Patient && rowData.Patient.Date_of_Surgery),
      customSort: (a, b) => {
        const aDate = a.Patient && a.Patient.Date_of_Surgery;
        const bDate = b.Patient && b.Patient.Date_of_Surgery;
        return moment(aDate).valueOf() - moment(bDate).valueOf();
      },
      cellStyle: {
        whiteSpace: 'nowrap',
      },
    },
    {
      title: 'Surgery Type',
      field: 'Patient.Surgery_Type',
      editable: 'never',
      render: (rowData) => (rowData.Patient && rowData.Patient.Surgery_Type) || '—',
    },
    {
      title: 'Surgery Side',
      field: 'Patient.Side_of_Body',
      editable: 'never',
      render: (rowData) => (rowData.Patient && rowData.Patient.Side_of_Body) || '—',
    },
    {
      title: 'Status',
      field: 'case_status',
      editable: 'never',
      export: false,
      lookup: {
        13: 'Aborted',
        Abort: 'Aborted',
        aborted: 'Aborted',
      },
      render: () => <span className={classes.statusBadge}>Aborted</span>,
      cellStyle: {
        textAlign: 'center',
        width: 145,
      },
      headerStyle: {
        textAlign: 'center',
      },
    },
  ], [classes.caseLink, classes.statusBadge, role]);

  if (loading) {
    return (
      <div className={classes.loadingScreen}>
        <div className={classes.loadingLogoWrap}>
          <img
            className={classes.loadingLogo}
            src={logo}
            alt="Lento Medical Innovation"
          />
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <main className={classes.page}>
        <div className={classes.errorCard}>{loadError}</div>
      </main>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <main className={classes.page}>
        <div className={classes.shell}>
          <section className={classes.hero}>
            <div>
              <p className={classes.eyebrow}>Case archive</p>
              <h1 className={classes.title}>Aborted Cases</h1>
              <p className={classes.subtitle}>
                Search, filter, and review cases removed from the active workflow.
              </p>
            </div>

            <div className={classes.countBadge} aria-label={`${tableData.length} aborted cases`}>
              <span className={classes.countValue}>{tableData.length}</span>
              <span className={classes.countLabel}>Aborted</span>
            </div>
          </section>

          <section className={classes.tableCard} aria-label="Aborted case list">
            <MaterialTable
              title="Aborted case records"
              columns={columns}
              data={tableData}
              icons={tableIcons}
              localization={{
                pagination: {
                  labelDisplayedRows: '{from}-{to} of {count}',
                  labelRowsSelect: 'rows',
                },
                toolbar: {
                  searchPlaceholder: 'Search aborted cases',
                  nRowsSelected: '{0} row(s) selected',
                },
                body: {
                  emptyDataSourceMessage: 'No aborted cases to display',
                  filterRow: {
                    filterTooltip: 'Filter',
                  },
                },
              }}
              options={{
                sorting: true,
                filtering: true,
                search: true,
                searchFieldAlignment: 'right',
                searchFieldVariant: 'standard',
                pageSize: 20,
                pageSizeOptions: [10, 20, 30, 50],
                paging: true,
                columnsButton: true,
                exportButton: true,
                exportAllData: true,
                showTitle: true,
                draggable: false,
                emptyRowsWhenPaging: false,
                tableLayout: 'auto',
                headerStyle: {
                  position: 'sticky',
                  top: 0,
                  zIndex: 2,
                  padding: '18px 20px',
                  background: '#f4eff0',
                  color: '#173247',
                  borderBottom: '1px solid #dfcfd2',
                  fontSize: 16.5,
                  fontWeight: 850,
                  whiteSpace: 'nowrap',
                },
                filterCellStyle: {
                  padding: '11px 16px',
                  background: '#fbf8f8',
                  fontSize: 15,
                },
                cellStyle: {
                  padding: '18px 20px',
                  color: '#264355',
                  fontSize: 16.5,
                  lineHeight: 1.5,
                  verticalAlign: 'middle',
                },
                rowStyle: (rowData, index) => ({
                  backgroundColor: index % 2 === 0 ? '#ffffff' : '#fbfafb',
                }),
              }}
              style={{
                width: '100%',
                minWidth: 1320,
                borderRadius: 0,
                boxShadow: 'none',
              }}
            />
          </section>
        </div>
      </main>
    </ThemeProvider>
  );
};

export default AbortList;